import React, { useState } from 'react';
import { AlertTriangle, Clock, Crown, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

interface TrialBannerProps {
  daysRemaining: number;
  isExpired: boolean;
  isTrialPlan?: boolean;
  hasNoPlan?: boolean;
  onDismiss?: () => void;
}

export const TrialBanner: React.FC<TrialBannerProps> = ({ daysRemaining, isExpired, isTrialPlan = true, hasNoPlan = false, onDismiss }) => {
  const navigate = useNavigate();
  const label = isTrialPlan ? 'trial' : 'subscription';
  const [dismissed, setDismissed] = useState(false);

  if (dismissed) return null;

  if (isExpired) {
    const message = hasNoPlan
      ? 'You are currently on no plan. Please select a plan to unlock all features.'
      : isTrialPlan
        ? 'Your trial has expired. Subscribe to continue using WeNext.'
        : 'Your subscription has expired. Renew to continue using WeNext.';

    const buttonLabel = hasNoPlan
      ? 'Select Plan'
      : isTrialPlan
        ? 'Subscribe Now'
        : 'Renew Now';

    return (
      <div className="w-full bg-red-600 text-white px-4 py-2.5 flex items-center justify-between gap-3 z-50">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />
          <span className="text-sm font-medium">
            {message}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            className="bg-white text-red-600 hover:bg-red-50 text-xs font-semibold flex-shrink-0"
            onClick={() => navigate('/home/account-settings?section=plans')}
          >
            <Crown className="h-3.5 w-3.5 mr-1" />
            {buttonLabel}
          </Button>
          <button
            onClick={() => { setDismissed(true); onDismiss?.(); }}
            className="p-1 rounded-full hover:bg-red-500 transition-colors flex-shrink-0"
            aria-label="Dismiss banner"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (daysRemaining > 5) return null;

  const urgencyColor = daysRemaining <= 2 ? 'bg-red-500' : daysRemaining <= 3 ? 'bg-orange-500' : 'bg-yellow-500';
  const textColor = daysRemaining <= 3 ? 'text-white' : 'text-yellow-900';

  return (
    <div className={`w-full ${urgencyColor} ${textColor} px-4 py-2 flex items-center justify-between gap-3 z-50`}>
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 flex-shrink-0" />
        <span className="text-sm font-medium">
          {daysRemaining === 0
            ? `Your ${label} expires today!`
            : daysRemaining === 1
              ? `Your ${label} expires tomorrow!`
              : `Your ${label} expires in ${daysRemaining} days.`
          }
          {' '}Subscribe to keep all your data and features.
        </span>
      </div>
      <Button
        size="sm"
        variant="secondary"
        className={`${daysRemaining <= 3 ? 'bg-white text-red-600 hover:bg-red-50' : 'bg-yellow-900 text-white hover:bg-yellow-800'} text-xs font-semibold flex-shrink-0`}
        onClick={() => navigate('/home/account-settings?section=plans')}
      >
        <Crown className="h-3.5 w-3.5 mr-1" />
        Upgrade
      </Button>
    </div>
  );
};
